export interface TutorialStep {
  id: string
  target: string              // CSS selector for spotlight target
  mobileTarget?: string       // Alternative CSS selector for mobile
  title: string               // Hebrew title
  description: string         // Hebrew description. Supports the {campaign} token,
                              // replaced with the currently open campaign's name.
  mobileDescription?: string  // Overrides `description` on mobile — for copy that only
                              // makes sense on one form factor (e.g. the bottom bar)
  desktopOnly?: boolean       // Step is dropped from the tutorial on mobile
  videoUrl?: string           // Optional video URL
  audioUrl?: string           // Optional audio URL
  tooltipPosition?: 'top' | 'bottom' | 'left' | 'right'
  mobileTooltipPosition?: 'top' | 'bottom' | 'left' | 'right'
  spotlightPadding?: number
  spotlightBorderRadius?: number
  advanceOn: 'click' | 'modal-close' | 'modal-close-repeat' | 'connect-profile' | 'connect-profile-iframe' | 'next-button'
  waitForTarget?: boolean     // Wait for target to appear in DOM before showing
  waitForSelector?: string    // Wait for this selector to be visible before showing (defaults to target if not set)
  modalId?: string            // Bootstrap modal ID to watch for close (e.g. 'kt_modal_1')
  customModalSelector?: string // Custom (non-Bootstrap) modal selector to watch for removal
  preventClose?: 'all' | 'backdrop-only'  // Block modal close: 'all' = only submit closes, 'backdrop-only' = block backdrop/escape but allow X
  hideCloseButtons?: string   // CSS selector(s) for close/cancel buttons to hide during this step
  scrollTarget?: string       // CSS selector for element to scroll into view (defaults to target)
  hoverImages?: Record<string, string | {url: string, caption: string}>  // Map of "quoted text" → image URL or {url, caption}
  showSkipTour?: boolean      // Show "דלג על המדריך" (skip tour) button on this step
  allowInteraction?: boolean  // Allow user to interact with the target element (don't block clicks)
  clickToReveal?: string      // CSS selector to click before showing step (e.g. click bar to open sheet)
  clickToRevealSelector?: string  // Wait for this selector to be visible after clicking clickToReveal
  mobileTopBanner?: boolean   // Force top banner on mobile instead of bottom sheet
  showDownArrow?: boolean     // Show bouncing down arrow pointing to target (for mobileTopBanner steps)
  requireCampaignSelected?: boolean // Disable "next" until a campaign is selected (URL is /campaigns/:id)
  secondaryButton?: { label: string; advanceBy: number } // Extra button that skips ahead by N steps
}

const fullTutorial: TutorialStep[] = [
  // Step 1: Click "create campaign"
  {
    id: 'click-create-campaign',
    target: '[data-onboarding="create-campaign"]',
    mobileTarget: '[data-onboarding="create-campaign-mobile"]',
    title: 'צור קמפיין',
    description: 'קמפיין הוא תיקייה שמארגנת את הפוסטים לפי עיר או תחום.',
    tooltipPosition: 'bottom',
    mobileTooltipPosition: 'top',
    spotlightPadding: 12,
    spotlightBorderRadius: 12,
    advanceOn: 'click',
  },
  // Step 2: Fill out campaign modal
  {
    id: 'fill-campaign-modal',
    target: '#kt_modal_1 .modal-content',
    title: 'מלא את פרטי הקמפיין',
    description: 'לדוגמה, אם יש לכם נכסים בתל אביב ונכסים ברמת גן,\nנפתח קמפיין אחד לתל אביב ונכניס אליו את כל הנכסים של תל אביב.\nבמקביל, נפתח קמפיין נוסף לרמת גן, ונכניס אליו את כל הנכסים של רמת גן.',
    videoUrl: 'https://zipper.ams3.cdn.digitaloceanspaces.com/tutorial/onboarding/campaign.mp4',
    tooltipPosition: 'left',
    spotlightPadding: 8,
    spotlightBorderRadius: 16,
    advanceOn: 'modal-close',
    waitForTarget: true,
    modalId: 'kt_modal_1',
    preventClose: 'all',
    hideCloseButtons: '#kt_modal_1 .btn-close, #kt_modal_1 [data-bs-dismiss="modal"]:not([type="submit"])',
  },
  // Step 3: Click add post button
  {
    id: 'click-add-post',
    target: '[data-onboarding="add-post"]',
    title: 'הוסף פוסט',
    description: 'לחץ כאן כדי ליצור פוסט חדש.',
    tooltipPosition: 'bottom',
    spotlightPadding: 10,
    spotlightBorderRadius: 12,
    advanceOn: 'click',
    waitForTarget: true,
  },
  // Step 5: Post creation modal (repeatable — user can add more posts)
  {
    id: 'fill-post-modal',
    target: '.fb-edit-modal',
    title: 'צור את הפוסט',
    description: 'כתוב את תוכן הפוסט, הוסף תמונות או סרטונים. כשתסיים, לחץ "פרסם".',
    videoUrl: 'https://zipper.ams3.cdn.digitaloceanspaces.com/tutorial/onboarding/posts.mp4',
    tooltipPosition: 'left',
    spotlightPadding: 8,
    spotlightBorderRadius: 16,
    advanceOn: 'modal-close-repeat',
    waitForTarget: true,
    customModalSelector: '.fb-edit-modal-backdrop',
    preventClose: 'all',
    hideCloseButtons: '.fb-edit-modal .fb-modal-close, .fb-edit-modal .fb-btn-secondary',
  },
  // Step 6: Show posts were added to the campaign
  {
    id: 'show-posts-added',
    target: '[data-onboarding="posts-button"]',
    mobileTarget: '#posts-section',
    title: 'הפוסטים נוספו בהצלחה!',
    description: 'הפוסטים שיצרתם נוספו לקמפיין "{campaign}".',
    tooltipPosition: 'bottom',
    mobileTooltipPosition: 'top',
    spotlightPadding: 8,
    spotlightBorderRadius: 8,
    advanceOn: 'next-button',
    waitForTarget: true,
    scrollTarget: '.campaign-card.active',
  },
  // Step 7: Click groups button
  {
    id: 'click-groups',
    target: '[data-onboarding="groups-button"]',
    mobileTarget: '[data-onboarding="groups-button-mobile"]',
    title: 'הקבוצות שלכם מוכנות ✅',
    description: 'כבר בחרנו עבורכם אוטומטית את הקבוצות הטובות ביותר לפרסום 🎯\nאפשר פשוט להמשיך — או ללחוץ על "קבוצות" כדי לראות ולערוך אותן.',
    tooltipPosition: 'top',
    spotlightPadding: 8,
    spotlightBorderRadius: 8,
    advanceOn: 'click', // clicking the groups button opens it and shows the how-to step
    allowInteraction: true,
    // Default path: skip the how-to (fill-groups-modal) and continue.
    secondaryButton: { label: 'להמשיך ➜', advanceBy: 2 },
  },
  // Step 7: Groups modal — unified view (libraries + manual search)
  {
    id: 'fill-groups-modal',
    target: '#kt_modal_8 .modal-content',
    title: 'בחירת קבוצות לפרסום',
    description: 'אם אתם עוסקים בתיווך, רכב או כוח אדם – המערכת בוחרת עבורכם אוטומטית ספריית קבוצות מותאמת לתחום.\nבנוסף, ניתן לחפש ולהוסיף קבוצות ידנית לפי עיר או תחום.\nאפשר לשלב בין ספריות לבחירה ידנית.\nהמערכת גם מצרפת את הפרופיל שלכם אוטומטית לקבוצות שאתם עדיין לא חברים בהן.\nכשתסיימו, לחצו על שמור.',
    videoUrl: 'https://zipper.ams3.cdn.digitaloceanspaces.com/tutorial/onboarding/groups.mp4',
    tooltipPosition: 'left',
    spotlightPadding: 8,
    spotlightBorderRadius: 16,
    advanceOn: 'modal-close',
    waitForTarget: true,
    modalId: 'kt_modal_8',
    preventClose: 'backdrop-only',
    hideCloseButtons: '#kt_modal_8 .sg-close-btn, #kt_modal_8 .sg-cancel-btn',
    allowInteraction: true,
  },
  // Step 10: Connect profile — login form
  {
    id: 'connect-profile',
    target: '[data-onboarding="connect-btn"]',
    title: 'חבר פרופיל פייסבוק',
    description: 'מזינים שם משתמש וסיסמה של פייסבוק – מייל או מספר נייד שאיתו מתחברים לפייסבוק – ולוחצים על "התחבר".\nאם שכחתם סיסמה, ניתן להיכנס לפייסבוק, ללחוץ על "שכחתי סיסמה" ולקבל סיסמה חדשה.\nאם רוצים לפרסם מהדף העסקי – חובה לחבר קודם את הפרופיל האישי. לאחר החיבור ניתן לבחור אם לפרסם מהפרופיל האישי, מהדף העסקי או משניהם במקביל.',
    videoUrl: 'https://zipper.ams3.cdn.digitaloceanspaces.com/tutorial/onboarding/profile.mp4',
    advanceOn: 'connect-profile',
  },
  // Step 11: Connect profile — iframe login view
  {
    id: 'connect-profile-iframe',
    target: '[data-onboarding="connect-btn"]',
    title: 'התחברות לפייסבוק',
    description: 'ייתכן שפייסבוק ישלח התראת התחברות.\nבמקרה כזה מאשרים בהתראות בפייסבוק "זה אני"\nאו שמבקשים קוד אימות בוואטסאפ או במייל דרך "another way" / דרך אחרת.\nמזינים את קוד האימות בן 6 הספרות במערכת – והפרופיל מתחבר.',
    hoverImages: {
      '"זה אני"': {
        url: '/media/tutorial/its-me.png',
        caption: 'ייתכן שפייסבוק ישלח התראת התחברות.\nבמקרה כזה מאשרים בהתראות בפייסבוק "זה אני"\nאו שמבקשים קוד אימות בוואטסאפ או במייל דרך "another way" / דרך אחרת.\nמזינים את קוד האימות בן 6 הספרות במערכת – והפרופיל מתחבר.',
      },
      '"another way"': '/media/tutorial/another-way.png',
    },
    advanceOn: 'connect-profile-iframe',
  },
  // Step 12: Dashboard tour — Profile section
  {
    id: 'tour-profiles',
    target: '.profile-cards-container',
    title: 'הפרופילים שלך',
    // The copy describes what this area is FOR, but the actions it names are blocked while
    // the tour is running (the picker is deliberately kept from opening over the step), so
    // it has to say when they become available — otherwise it reads as a broken screen.
    description: 'כאן מוצגים כל הפרופילים המחוברים למערכת.\nניתן לחבר פרופילים נוספים, לנהל דפים עסקיים ולרכוש פרופילים ודפים נוספים.\nℹ️ הוספת פרופילים או דפים תתאפשר רק לאחר סיום המדריך.',
    videoUrl: 'https://zipper.ams3.cdn.digitaloceanspaces.com/tutorial/onboarding/overview.mp4',
    advanceOn: 'next-button',
    tooltipPosition: 'bottom',
    waitForTarget: true,
    showSkipTour: true,
  },
  // Step 13: Dashboard tour — Campaign bar (mobile: show how to open panel)
  {
    id: 'tour-campaign-card',
    target: '.campaign-card.active',
    mobileTarget: '.mobile-campaign-bar',
    title: 'ניהול קמפיינים',
    description: 'כאן מנהלים את הקמפיין – בוחרים את הפרופילים, רואים מה הסטטוס, כמה פוסטים וקבוצות יש בו, ומפעילים או משביתים את הפרסום בלחיצה על המתג.',
    // Only mobile has the bottom bar, so only mobile gets told about it — and it now
    // names the button instead of describing where it sits.
    mobileDescription: 'כאן מנהלים את הקמפיין – בוחרים את הפרופילים, רואים מה הסטטוס, כמה פוסטים וקבוצות יש בו, ומפעילים או משביתים את הפרסום בלחיצה על המתג.\nכפתור "החלף קמפיין" שבתחתית המסך מעביר אתכם בין הקמפיינים.',
    advanceOn: 'next-button',
    tooltipPosition: 'bottom',
    mobileTooltipPosition: 'top',
    scrollTarget: '.campaign-card.active',
    showSkipTour: true,
    mobileTopBanner: true,
  },
  // Step 14: Dashboard tour — Campaign stats (mobile: open panel to show campaigns)
  {
    id: 'tour-campaign-stats',
    target: '[data-onboarding="campaign-stats-loaded"]',
    mobileTarget: '.campaign-sheet',
    title: 'פוסטים וקבוצות',
    description: '📊 פוסטים – כמה פוסטים יש בקמפיין. לחיצה פותחת את רשימת הפוסטים.\n👥 קבוצות – כמה קבוצות נבחרו לפרסום. לחיצה פותחת את בחירת הקבוצות.\n📈 פורסמו – סה״כ פרסומים שבוצעו מאז תחילת הקמפיין.\n📅 היום – כמה פרסומים בוצעו היום.',
    advanceOn: 'next-button',
    tooltipPosition: 'bottom',
    mobileTooltipPosition: 'top',
    scrollTarget: '.campaign-card.active',
    showSkipTour: true,
    clickToReveal: '.mobile-campaign-bar',
    clickToRevealSelector: '.campaign-sheet.show',
    // On mobile the tour ends after the campaign card — the remaining stops repeat
    // what the bottom sheet already showed and drag the flow out on a small screen.
    desktopOnly: true,
  },
  // Step 15: Dashboard tour — Posts section
  {
    id: 'tour-posts-section',
    target: '#posts-section',
    title: 'אזור הפוסטים',
    description: 'כאן מוצגים כל הפוסטים של הקמפיין.\nאפשר ליצור פוסטים חדשים, לערוך קיימים, לשכפל או למחוק.\nכל פוסט מתפרסם אוטומטית בקבוצות שבחרתם.',
    advanceOn: 'next-button',
    tooltipPosition: 'top',
    showSkipTour: true,
    mobileTopBanner: true,
    desktopOnly: true,
  },
  // Step 16: Dashboard tour — Support widget + final
  {
    id: 'tour-support',
    target: '.support-widget-button',
    title: 'אנחנו כאן בשבילכם!',
    description: 'ההצלחה שלכם היא ההצלחה שלנו.\nאם יש שאלה, בעיה או סתם צריכים עזרה – לחצו כאן ונחזור אליכם בהקדם.\nצוות FB Zipper תמיד כאן לעזור.',
    advanceOn: 'next-button',
    tooltipPosition: 'top',
    showSkipTour: true,
    mobileTopBanner: true,
    showDownArrow: true,
    spotlightPadding: 12,
    spotlightBorderRadius: 50,
    desktopOnly: true,
  },
]

// Post-onboarding: campaigns were ALREADY created for the user during the onboarding
// wizard, so instead of the "create campaign" steps we ask them to pick one to start with.
const selectCampaignStep: TutorialStep = {
  id: 'select-campaign',
  target: '.campaigns-cards-container',   // desktop: the list of campaign cards
  mobileTarget: '.mobile-campaign-bar',   // mobile: the sticky campaign bar (opens the sheet)
  title: 'בחרו קמפיין להתחלה',
  description: 'כבר הכנו עבורכם קמפיין לכל עיר שבחרתם 🎉\n👆 לחצו על אחד הקמפיינים כדי לבחור אותו (אפשר להחליף בכל רגע), ואז לחצו "המשך".',
  // The banner is one tight row on a phone, so the desktop copy wraps to five or six
  // lines and the banner ends up eating a third of the screen — and it pushes the page
  // down by its own height, so the campaigns it's asking you to tap get shoved off.
  mobileDescription: '👆 לחצו על קמפיין כדי לבחור אותו',
  advanceOn: 'next-button',               // they pick a campaign (navigation keeps ?tut), then continue
  allowInteraction: true,                 // let them actually click a campaign
  requireCampaignSelected: true,          // "המשך" stays disabled until a campaign is chosen
  // Above the cards, not below: the instruction is "pick one of these", so it has to be
  // read before the eye lands on the list — and below it the tooltip covers the cards
  // further down the page that it's asking them to click.
  tooltipPosition: 'top',
  mobileTooltipPosition: 'top',
  waitForTarget: true,
  scrollTarget: '.campaigns-cards-container',
  mobileTopBanner: true,
  clickToReveal: '.mobile-campaign-bar',        // mobile: auto-open the sheet so campaigns are visible
  clickToRevealSelector: '.campaign-sheet.show',
  spotlightPadding: 10,
  spotlightBorderRadius: 12,
}

// Connecting the Facebook profile now happens in the post-register wizard
// (PostRegisterOnboarding's `fbconnect` step), so anyone arriving on the `onboarded`
// tour has already done it — walking them through it again is a dead step they can only
// sit and stare at. Dropped from that variant only: `full` still carries it for accounts
// that never went through the new wizard, and the index-based sub-tutorials below still
// need fullTutorial's numbering intact, so this filters rather than removing the steps.
const CONNECT_STEP_IDS = ['connect-profile', 'connect-profile-iframe']

export const tutorials: Record<string, TutorialStep[]> = {
  full: fullTutorial,
  // Same as `full` but starts by SELECTING an existing campaign instead of creating one.
  onboarded: [
    selectCampaignStep,
    ...fullTutorial.slice(2).filter((s) => !CONNECT_STEP_IDS.includes(s.id)),
  ],
  profiles: [fullTutorial[7]],
  'create-campaign': [fullTutorial[0], fullTutorial[1]],
  'create-posts': [fullTutorial[2], fullTutorial[3], fullTutorial[4]],
  groups: [fullTutorial[5], fullTutorial[6]],
}
